import { ToastContext } from "context/ToastContext";
import { Checkbox } from "primereact/checkbox";
import { FileUpload } from "primereact/fileupload";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { useContext, useRef, useState } from "react";
import { useSplitRateFileMutation } from "store/queries/rateTools";
import styled from "styled-components";

import { fileModeLabel, ISplitStats, parseSplitStats } from "./utils";

// PrimeReact does not export the upload-handler event type, so we restate the only
// shape we read from the event here. (`uploadHandler?(e: FileUploadHandlerParam)`,
// where `FileUploadHandlerParam extends { files: File[] }`.)
interface IFileUploadHandlerEvent {
  files: File[];
}

/**
 * CLE-2323 — Rate File Splitter sub-tab on PayerRules → Tools.
 *
 * Upload one physician rate file (CSV or XLSX); the API auto-detects file shape
 * (Aetna-style balanced vs BCBS-style facility-as-exception), normalizes MOD/POS
 * values, and streams back a ZIP with `facility.csv` + `non-facility.csv`. The
 * analyst toggles which outputs to include and whether to carry through the
 * `Service Line` column.
 *
 * Diagnostic stats (detected mode, row counts) come back in `x-split-*` response
 * headers and are surfaced in a results banner so the user can sanity-check what the
 * splitter did before opening the downloaded ZIP.
 */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 720px;
`;

const Toggles = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }
`;

export function RateFileSplitter() {
  const { current: toastElement } = useContext(ToastContext);
  const fileUploadRef = useRef<FileUpload>(null);

  const [includeFacility, setIncludeFacility] = useState(true);
  const [includeNonFacility, setIncludeNonFacility] = useState(true);
  const [includeServiceLine, setIncludeServiceLine] = useState(true);
  const [lastStats, setLastStats] = useState<ISplitStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [splitFile, { isLoading }] = useSplitRateFileMutation();

  const submit = async (event: IFileUploadHandlerEvent) => {
    const file = event.files[0];
    if (!file) {
      // PrimeReact's FileUpload fires the upload handler even when nothing has been
      // chosen — without this guard, the backend cheerfully parses an empty payload
      // and returns "Detected as unknown / 0 rows" (Ani CLE-2323 follow-up).
      toastElement?.show({
        severity: "warn",
        summary: "No file selected",
        detail: "Choose a rate file first, then click Split.",
      });
      return;
    }
    if (!includeFacility && !includeNonFacility) {
      toastElement?.show({
        severity: "warn",
        summary: "Pick an output",
        detail: "Choose at least one of facility / non-facility.",
      });
      return;
    }

    setLastStats(null);
    setErrorMessage(null);

    try {
      const result = await splitFile({
        file,
        includeFacility,
        includeNonFacility,
        includeServiceLine,
      }).unwrap();

      setLastStats(parseSplitStats(result.headers));

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      const inferredName = (file.name || "split").replace(/\.[^.]+$/, "");
      link.download = `${inferredName}-split.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toastElement?.show({
        severity: "success",
        summary: "Split complete",
        detail: "Your ZIP is downloading.",
      });
      fileUploadRef.current?.clear();
    } catch (e: any) {
      const detail =
        e?.data?.message ??
        e?.data?.error ??
        (typeof e?.data === "string" ? e.data : null) ??
        e?.message ??
        "Split failed.";
      setErrorMessage(detail);
    }
  };

  return (
    <Wrapper>
      <h3>Split Physician Rate File</h3>
      <p style={{ margin: 0, color: "var(--gray-600)" }}>
        Upload one physician rate file (CSV or XLSX). The tool auto-detects the
        file shape, normalizes MOD/POS values, and returns a ZIP containing the
        facility rates and the non-facility (office) rates as separate CSVs.
      </p>
      <details style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
        <summary style={{ cursor: "pointer" }}>Expected columns</summary>
        <ul style={{ margin: "0.5rem 0 0 1.2rem", padding: 0 }}>
          <li>
            <strong>CPT code</strong> (required) — header can be any of:{" "}
            <code>CPT Code</code>, <code>CPT/HCPCS</code>,{" "}
            <code>Procedure Code</code>, <code>HCPCS</code>,{" "}
            <code>Proc_CD</code>, <code>CPT</code>
          </li>
          <li>
            <strong>Rate</strong> (required) — any of: <code>Rate</code>,{" "}
            <code>Fee Amount</code>, <code>Base Rate</code>,{" "}
            <code>Calculated rate</code>
          </li>
          <li>
            <strong>MOD / Modifier</strong> (optional)
          </li>
          <li>
            <strong>POS / Place of Service / Site Of Service</strong> (optional)
          </li>
          <li>
            <strong>Service Line / Type of Service</strong> (optional)
          </li>
          <li>
            <strong>Description / CPT Name</strong> (optional)
          </li>
        </ul>
        <p style={{ margin: "0.5rem 0 0 0" }}>
          Hyphens and underscores in header names are treated as spaces (so{" "}
          <code>Service-Line</code> and <code>Service_Line</code> both match{" "}
          <code>Service Line</code>).
        </p>
      </details>

      <Toggles>
        <label>
          <Checkbox
            checked={includeFacility}
            onChange={(e) => setIncludeFacility(e.checked ?? false)}
          />
          Include facility output
        </label>
        <label>
          <Checkbox
            checked={includeNonFacility}
            onChange={(e) => setIncludeNonFacility(e.checked ?? false)}
          />
          Include non-facility output
        </label>
        <label>
          <Checkbox
            checked={includeServiceLine}
            onChange={(e) => setIncludeServiceLine(e.checked ?? false)}
          />
          Include Service Line column
        </label>
      </Toggles>

      <FileUpload
        ref={fileUploadRef}
        name="file"
        accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        customUpload
        uploadHandler={submit}
        maxFileSize={50 * 1024 * 1024}
        disabled={isLoading}
        chooseLabel="Choose file"
        uploadLabel="Split"
      />

      {isLoading && (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      )}

      {errorMessage && (
        <Message
          severity="error"
          text={errorMessage}
          style={{ alignItems: "flex-start" }}
        />
      )}

      {lastStats && !errorMessage && lastStats.inputRows === 0 && (
        <Message
          severity="warn"
          content={
            <div>
              <strong>File parsed but no data rows were found.</strong>
              <div style={{ marginTop: "0.25rem" }}>
                Check that the file has the expected columns (see &quot;Expected
                columns&quot; above) and at least one row of data.
              </div>
            </div>
          }
        />
      )}

      {lastStats && !errorMessage && lastStats.inputRows > 0 && (
        <Message
          severity="success"
          content={
            <div>
              <div>
                <strong>{fileModeLabel(lastStats.fileMode)}</strong>
              </div>
              <div>
                {lastStats.inputRows.toLocaleString()} input rows →{" "}
                {lastStats.facilityRows.toLocaleString()} facility +{" "}
                {lastStats.nonFacilityRows.toLocaleString()} non-facility output
                rows ({lastStats.distinctCpts.toLocaleString()} distinct CPTs)
              </div>
              {(lastStats.droppedMissingCpt > 0 ||
                lastStats.droppedNoRate > 0) && (
                <div style={{ color: "var(--gray-700)", fontSize: "0.9em" }}>
                  Dropped: {lastStats.droppedMissingCpt} rows with no CPT,{" "}
                  {lastStats.droppedNoRate} rows with no rate
                </div>
              )}
            </div>
          }
        />
      )}
    </Wrapper>
  );
}

export default RateFileSplitter;
